import React, { useState } from "react";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../../../utils/api";

/* ─────────────────────────────────────────────────────────────────
   SearchFeedback
   • Single-button mode: pass explicit `kind` + `label` (TopicBrief)
   • Dual-button mode: no `kind` → shows "Not relevant" + "Accuracy issue"
   ───────────────────────────────────────────────────────────────── */

const useFeedbackSend = (query, itemId) => {
  const { user } = useAuth();
  const [states, setStates] = useState({});    // { [kind]: "idle"|"note"|"sending"|"sent"|"error" }
  const [note, setNote]     = useState("");

  const get = (kind) => states[kind] || "idle";

  const send = async (kind, noteText = "") => {
    if (!user) { setStates(s => ({ ...s, [kind]: "signin" })); return; }
    setStates(s => ({ ...s, [kind]: "sending" }));
    try {
      await apiFetch("/api/search/feedback", {
        method: "POST",
        body: JSON.stringify({ query, item_id: itemId || null, kind, note: noteText }),
      });
      setStates(s => ({ ...s, [kind]: "sent" }));
      setNote("");
    } catch {
      setStates(s => ({ ...s, [kind]: "error" }));
    }
  };

  return { get, send, note, setNote, states };
};

/* Single-button mode — backward compatible with TopicBrief */
const SingleFeedback = ({ query, itemId, kind, label, prefix }) => {
  const { get, send, states } = useFeedbackSend(query, itemId);
  const st = get(kind);
  return (
    <div className="text-xs mt-3">
      <button
        data-testid={`${prefix}-button`}
        disabled={st === "sending" || st === "sent"}
        onClick={() => send(kind)}
        className="underline underline-offset-4 min-h-11 text-[var(--muted)] hover:text-[var(--crimson)] disabled:opacity-50"
      >
        {st === "sending" ? "Sending…" : label}
      </button>
      {(st === "sent" || states[kind] === "signin" || st === "error") && (
        <span role="status" data-testid={`${prefix}-status`} className="block text-[var(--muted)]">
          {st === "sent"   ? "Sent to the editorial team." :
           st === "error"  ? "Could not send. Please retry." :
                             "Sign in to send feedback."}
        </span>
      )}
    </div>
  );
};

/* Dual-button mode — used below search result cards.
   chips=true → compact pill styling for use inside HighlightCard footer */
const DualFeedback = ({ query, itemId, prefix, chips = false }) => {
  const { get, send, note, setNote, states } = useFeedbackSend(query, itemId);
  const [noteOpen, setNoteOpen] = useState(false);
  const anySignin = Object.values(states).includes("signin");
  const anyError  = Object.values(states).includes("error");

  const handleAccuracyClick = () => {
    if (get("accuracy") === "idle") { setNoteOpen(true); return; }
  };

  const submitAccuracy = () => { send("accuracy", note); setNoteOpen(false); };

  /* chip style vs plain-text style */
  const chipCls = chips
    ? "inline-flex items-center h-6 px-2.5 rounded-full border border-[var(--hairline)] font-mono text-[9px] tracking-[0.05em] uppercase text-[var(--muted)] hover:border-[var(--crimson)] hover:text-[var(--crimson)] transition-colors disabled:opacity-40 shrink-0"
    : "underline underline-offset-4 min-h-9 text-[var(--muted)] hover:text-[var(--crimson)] disabled:opacity-50";
  const wrapCls = chips
    ? "flex flex-wrap gap-1.5 pt-2"
    : "text-xs mt-3 space-y-1.5";
  const rowCls  = chips ? "flex flex-wrap gap-1.5" : "flex flex-wrap gap-x-4 gap-y-1";

  return (
    <div className={wrapCls} onClick={e => e.stopPropagation()}>
      <div className={rowCls}>
        {/* Not relevant */}
        {get("not_relevant") === "sent"
          ? <span role="status" data-testid={`${prefix}-not_relevant-status`} className={chips ? "font-mono text-[9px] text-[var(--muted)]" : "text-[var(--muted)] min-h-9 flex items-center"}>Sent</span>
          : <button data-testid={`${prefix}-not_relevant-button`} disabled={get("not_relevant") === "sending"} onClick={() => send("not_relevant")} className={chipCls}>{get("not_relevant") === "sending" ? "Sending…" : "Not relevant"}</button>
        }
        {/* Accuracy issue */}
        {get("accuracy") === "sent"
          ? <span role="status" data-testid={`${prefix}-accuracy-status`} className={chips ? "font-mono text-[9px] text-[var(--muted)]" : "text-[var(--muted)] min-h-9 flex items-center"}>Sent</span>
          : <button data-testid={`${prefix}-accuracy-button`} disabled={get("accuracy") === "sending"} onClick={handleAccuracyClick} className={chipCls}>{get("accuracy") === "sending" ? "Sending…" : "Accuracy issue"}</button>
        }
      </div>

      {/* Expandable note for accuracy reports */}
      {noteOpen && get("accuracy") !== "sent" && (
        <div className="flex flex-col gap-1.5 pt-1 w-full" data-testid={`${prefix}-accuracy-form`}>
          <textarea
            data-testid={`${prefix}-accuracy-note`}
            placeholder="Briefly describe the concern (optional, max 500 chars)"
            value={note}
            onChange={e => setNote(e.target.value.slice(0, 500))}
            rows={2}
            className="text-xs border border-[var(--hairline)] rounded p-2 resize-none bg-transparent w-full"
          />
          <div className="flex gap-3">
            <button data-testid={`${prefix}-accuracy-submit`} onClick={submitAccuracy} className="underline underline-offset-4 text-[var(--crimson)] text-xs min-h-8">Submit</button>
            <button data-testid={`${prefix}-accuracy-cancel`} onClick={() => { setNoteOpen(false); setNote(""); }} className="underline underline-offset-4 text-[var(--muted)] text-xs min-h-8">Cancel</button>
          </div>
        </div>
      )}

      {anySignin && <span role="status" data-testid={`${prefix}-signin-prompt`} className="block text-[9px] text-[var(--muted)]">Sign in to send.</span>}
      {anyError  && <span role="status" data-testid={`${prefix}-error`} className="block text-[9px] text-[var(--muted)]">Could not send.</span>}
    </div>
  );
};

export const SearchFeedback = ({ query, itemId, kind, label = "Not relevant", prefix = "search-feedback", chips = false }) => {
  /* If a specific kind is supplied, use single-button (TopicBrief compatibility) */
  if (kind) return <SingleFeedback query={query} itemId={itemId} kind={kind} label={label} prefix={prefix} />;
  return <DualFeedback query={query} itemId={itemId} prefix={prefix} chips={chips} />;
};